import { Form, Button, message } from "antd";
import { useState, useEffect } from "react";
import { createRecipe, updateRecipe } from "../../services/recipe.service";

import FoodDetails from "./FoodDetails";
import NutritionSection from "./NutritionSection";
import IngredientsBuilder from "./IngredientsBuilder";
import DirectionsBuilder from "./DirectionsBuilder";

export default function RecipeForm({ mode = "add", initialData = null, onSuccess }) {
  const [form] = Form.useForm();

  const [coverImage, setCoverImage] = useState(null);
  const [mediaVideoFile, setMediaVideoFile] = useState(null);
  const [videoRemoved, setVideoRemoved] = useState(false);
  const [additionalNutrition, setAdditionalNutrition] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === "edit" && initialData) {
      const recipe = initialData;
      const isHours = recipe.totalTime >= 60;

      form.setFieldsValue({
        title: recipe.title,
        titleAr: recipe.i18n?.ar?.title || "",
        description: recipe.description,
        descriptionAr: recipe.i18n?.ar?.description || "",
        dietType: recipe.dietType,
        cuisineType: recipe.cuisineType,
        mealType: recipe.mealType
          ? Array.isArray(recipe.mealType)
            ? recipe.mealType
            : [recipe.mealType]
          : [],
        servings: recipe.servings,
        totalTimeValue: isHours
          ? Math.floor(recipe.totalTime / 60)
          : recipe.totalTime,
        totalTimeUnit: isHours ? "Hours" : "Minutes",
        nutrition: {
          calories: recipe.nutrition?.calories,
          protein: recipe.nutrition?.protein,
          carbs: recipe.nutrition?.carbs,
          fat: recipe.nutrition?.fat,
        },
      });

      setAdditionalNutrition(recipe.nutrition?.additional || []);
      setIngredients(recipe.ingredients || []);
      setSteps(recipe.steps || []);
      setMediaVideoFile(null);
      setVideoRemoved(false);
    }
  }, [initialData, mode, form]);

  const handleSubmit = async (values) => {
    if (loading) return;

    if (!ingredients.length) {
      return message.error("Please add at least 1 ingredient");
    }

    if (!steps.length) {
      return message.error("Please add at least 1 step");
    }

    if (!values.totalTimeValue || values.totalTimeValue <= 0) {
      return message.error("Enter valid cooking time");
    }

    setLoading(true);

    try {
      const formData = new FormData();
      const rawTime = Number(values.totalTimeValue);
      const safeTime = isNaN(rawTime) ? 0 : rawTime;

      const totalTimeInMinutes =
        values.totalTimeUnit === "Hours" ? safeTime * 60 : safeTime;

      const skipKeys = new Set([
        "totalTimeValue",
        "totalTimeUnit",
        "nutrition",
        "coverImage",
        "titleAr",
        "descriptionAr",
      ]);

      Object.entries(values).forEach(([key, value]) => {
        if (value !== undefined && value !== "" && !skipKeys.has(key)) {
          if (key === "mealType") {
            formData.append("mealType", JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        }
      });

      formData.append("totalTime", totalTimeInMinutes);

      // Always send i18n so edit can clear Arabic overlays.
      formData.append(
        "i18n",
        JSON.stringify({
          ar: {
            title: (values.titleAr || "").trim(),
            description: (values.descriptionAr || "").trim(),
          },
        })
      );

      if (values.nutrition) {
        const nutritionPayload = {
          ...values.nutrition,
          additional: additionalNutrition,
        };
        formData.append("nutrition", JSON.stringify(nutritionPayload));
      }

      formData.append("ingredients", JSON.stringify(ingredients));
      formData.append("steps", JSON.stringify(steps));

      if (coverImage) {
        formData.append("coverImage", coverImage);
      } else if (mode === "edit" && initialData?.coverImage) {
        formData.append("coverImage", initialData.coverImage);
      }

      const hasRecipeVideo =
        !!mediaVideoFile ||
        (mode === "edit" && !!initialData?.mediaUrl && !videoRemoved);

      formData.append("mediaType", hasRecipeVideo ? "video" : "image");

      if (mediaVideoFile) {
        formData.append("mediaUrl", mediaVideoFile);
      } else if (mode === "edit" && initialData?.mediaUrl && !videoRemoved) {
        formData.append("mediaUrl", initialData.mediaUrl);
      }

      let res;

      if (mode === "add") {
        res = await createRecipe(formData);
        message.success(res?.msg || "Recipe added successfully");
      } else {
        res = await updateRecipe(initialData._id, formData);
        message.success(res?.msg || "Recipe updated successfully");
      }

      if (mode === "add") {
        form.resetFields();
        setIngredients([]);
        setSteps([]);
        setAdditionalNutrition([]);
        setCoverImage(null);
        setMediaVideoFile(null);
        setVideoRemoved(false);
      }

      onSuccess?.();
    } catch (err) {
      console.log(err);
      message.error(err?.msg || "Failed to save recipe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={handleSubmit}
      disabled={loading}
      scrollToFirstError
    >
      <FoodDetails
        form={form}
        setCoverImage={setCoverImage}
        initialImage={initialData?.coverImage}
        isCoverRequired={
          mode === "add" || (mode === "edit" && !initialData?.coverImage)
        }
        mediaVideoFile={mediaVideoFile}
        setMediaVideoFile={setMediaVideoFile}
        initialMediaUrl={initialData?.mediaUrl}
        videoRemoved={videoRemoved}
        setVideoRemoved={setVideoRemoved}
      />

      <NutritionSection
        setAdditionalNutrition={setAdditionalNutrition}
        initialAdditional={initialData?.nutrition?.additional || []}
      />

      <IngredientsBuilder
        setIngredients={setIngredients}
        initialIngredients={initialData?.ingredients || []}
      />

      <DirectionsBuilder
        setSteps={setSteps}
        initialSteps={initialData?.steps || []}
      />

      <div className="add-recipe-footer">
        <Button
          type="primary"
          size="large"
          className="submit-btn"
          htmlType="submit"
          loading={loading}
          disabled={loading}
        >
          {mode === "add" ? "Add Recipe" : "Update Recipe"}
        </Button>
      </div>
    </Form>
  );
}
